import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dataService from 'services/dataService';
import { API_PODS } from 'services/dataService/resources';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';

import { ApiError } from '../utils/api-error';
import { classifyPodError } from '../utils/pod-error-classifier';

import type { CreatePodResponse } from '../types';

type PodStatus = 'active' | 'inactive';

interface IUseTogglePodStatusOptions {
  onSuccess?: () => void;
}

export const useTogglePodStatus = ({
  onSuccess,
}: IUseTogglePodStatusOptions = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: async ({
      guid,
      status,
    }: {
      guid: string;
      status: PodStatus;
    }) => {
      const {
        data,
        error,
        status: httpStatus,
      } = await dataService.patchOne<CreatePodResponse>(API_PODS, guid, {
        status,
      });
      if (error) {
        throw new ApiError(error, httpStatus);
      }
      return { data, requestedStatus: status };
    },
    onSuccess: ({ requestedStatus }) => {
      const messageKey =
        requestedStatus === 'inactive'
          ? 'labels.podInactivatedSuccessfully'
          : 'labels.podActivatedSuccessfully';
      const fallback =
        requestedStatus === 'inactive'
          ? 'POD inactivated successfully'
          : 'POD activated successfully';

      notificationController.success({
        message: t(messageKey, fallback),
      });

      queryClient.invalidateQueries({ queryKey: [API_PODS, 'list'] });
      queryClient.invalidateQueries({ queryKey: [API_PODS, 'detail'] });

      onSuccess?.();
    },
    onError: (error: unknown) => {
      const classified = classifyPodError(error);

      if (classified.type === 'POD_NOT_FOUND') {
        notificationController.error({
          message: classified.messageKey
            ? t(classified.messageKey, classified.fallback)
            : classified.fallback,
        });
        queryClient.invalidateQueries({ queryKey: [API_PODS, 'list'] });
        return;
      }

      if (classified.type === 'INSUFFICIENT_PRIVILEGES') {
        notificationController.error({
          message: classified.messageKey
            ? t(classified.messageKey, classified.fallback)
            : classified.fallback,
        });
        return;
      }

      // VALIDATION_ERROR (400): surface backend's raw message
      if (classified.type === 'VALIDATION_ERROR') {
        notificationController.error({
          message: classified.fallback,
        });
        return;
      }

      // GENERIC (500+/unknown) and any other type: use toggle-specific message
      notificationController.error({
        message: t(
          'labels.unableToUpdatePodStatus',
          'Unable to update Pod status. Please try again.',
        ),
      });
    },
  });

  const toggleStatus = (
    guid: string,
    newStatus: PodStatus,
    callbacks?: { onSettled?: () => void },
  ) => {
    mutate({ guid, status: newStatus }, { onSettled: callbacks?.onSettled });
  };

  return { toggleStatus, isLoading };
};
